import {
  clockHM,
  dueTone,
  formatLongDate,
  toDate,
  weekdayName,
  type CalendarAllDayEvent,
  type CalendarBlock,
  type CalendarDueItem,
  type ISODate,
} from '@app/shared'

import { BlockChip, GhostSlot, GridColumn, NowLine, TimeGutter, useGridScroll } from './grid'
import { PALETTE, pillMotion } from './palette'
import { anchorOfElement, type PeekAnchor } from '../../lib/popover'
import type { BlockDrag, BlockDragMode } from './useBlockDrag'

// ============================================================================
// The day grid — docs/design/handoff/"LifeOS Calendar.dc.html" option 11b.
//
// One column, a 58px gutter, wider blocks with room for a lock and a provenance stamp. The
// deadlines that the week view puts in an all-day row appear here as pills in the day's header,
// because one day does not need a row of its own to hold two chips.
//
// THE DROP GHOST IS THE INTERACTION'S HONESTY. A drag says what time it is about to take before
// it takes it, and the drop snaps to 15 minutes (brief → Day view extras). Motion and feel are
// the one thing no gate in this repo can see; they are named in the port report.
// ============================================================================

const GUTTER = 58

export interface DragProposal {
  label: string
  startMinutes: number
  endMinutes: number
}

export function DayView({
  date,
  today,
  nowMinutes,
  blocks,
  dueItems,
  allDayEvents = [],
  proposal,
  drag,
  onOpenBlock,
  onOpenDue,
  onOpenAllDay,
  onCompose,
  onDropAt,
  onDragBegin,
  suggestionActions,
}: {
  date: ISODate
  today: ISODate | undefined
  nowMinutes: number | null
  blocks: CalendarBlock[]
  dueItems: CalendarDueItem[]
  /**
   * Google's whole-day events for this day. They ride in the header beside the deadlines, for
   * the same reason the deadlines do: neither has a time of day, and the day view has no all-day
   * row of its own (the brief puts one in the week view only).
   */
  allDayEvents?: CalendarAllDayEvent[]
  proposal: DragProposal | null
  /** A block being moved or resized on THIS grid, or null. Its proposal is drawn as a ghost. */
  drag: BlockDrag | null
  onOpenBlock: (block: CalendarBlock, anchor: PeekAnchor) => void
  /** The header's due pills open the detail peek — Canvas's own deadline, no edit fields. */
  onOpenDue: (item: CalendarDueItem, anchor: PeekAnchor) => void
  /** The header's whole-day pills, same peek, Google's own whole-day facts. */
  onOpenAllDay: (event: CalendarAllDayEvent, anchor: PeekAnchor) => void
  /** The minute the click landed on, and the point it landed at — the editor opens beside it. */
  onCompose: (minutes: number, anchor: PeekAnchor) => void
  onDropAt: (minutes: number, event: React.DragEvent<HTMLDivElement>) => void
  onDragBegin: (
    block: CalendarBlock,
    mode: BlockDragMode,
    event: React.PointerEvent<HTMLElement>,
  ) => void
  /** Keep / Dismiss, rendered inside a suggested block. */
  suggestionActions: (block: CalendarBlock) => React.ReactNode
}) {
  const isToday = date === today
  const body = useGridScroll()

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-panel border border-line-2 bg-surface shadow-calendar">
      <div className="flex flex-none items-center gap-[11px] border-b border-line-4 px-5 pb-[11px] pt-[13px]">
        <span className="text-[13px] font-semibold leading-none text-ink">
          {weekdayName(date)} {toDate(date).getUTCDate()}
        </span>

        {dueItems.map((item) => {
          const palette = PALETTE[today ? dueTone(item, today) : 'canvas']
          return (
            <button
              key={item.id}
              type="button"
              onClick={(e) => onOpenDue(item, anchorOfElement(e.currentTarget))}
              // 12d PICKS THE MECHANIC BY RADIUS, and these header chips are radius 14 —
              // fully rounded — so they take `.mo-ink`, the tint filling from the left, and NOT
              // the straight rule the grid blocks below them wear. A straight rule on a pill
              // follows the curve and reads as a crescent (index.css → THE MOTION SYSTEM).
              style={pillMotion(palette)}
              className={[
                'mo-ink mo-press rounded-[14px] px-2 py-[3px] text-[10px] font-semibold leading-[1.4]',
                palette.tint,
                palette.ink,
              ].join(' ')}
              title={`From Canvas${item.courseCode ? ` · ${item.courseCode}` : ''}`}
            >
              {item.label} · Canvas
            </button>
          )
        })}

        {allDayEvents.map((event) => (
          <button
            key={event.id}
            type="button"
            onClick={(e) => onOpenAllDay(event, anchorOfElement(e.currentTarget))}
            style={pillMotion(PALETTE.google)}
            className={[
              'mo-ink mo-press rounded-[14px] px-2 py-[3px] text-[10px] font-semibold leading-[1.4]',
              PALETTE.google.tint,
              PALETTE.google.ink,
              event.status === 'tentative' ? 'italic' : '',
            ].join(' ')}
            title={[
              'All day, from Google Calendar',
              event.location ?? undefined,
              event.status === 'tentative' ? 'marked tentative in Google' : undefined,
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            {/* "All day" is Google's own fact, not a time this screen picked. */}
            {event.title} · all day · Google
          </button>
        ))}

        {isToday && nowMinutes !== null && (
          <span className="ml-auto text-[10.5px] font-normal leading-none text-microcopy">
            Now {clockHM(nowMinutes)} {nowMinutes < 720 ? 'AM' : 'PM'}
          </span>
        )}
        {!isToday && (
          <span className="ml-auto text-[10.5px] font-normal leading-none text-microcopy">
            {formatLongDate(date)}
          </span>
        )}
      </div>

      {/* THE BODY SCROLLS, and it opens on the morning — see `useGridScroll`. Twenty-four hours
          at 46px is 1104px, which no card on this screen is tall enough to show at once, and
          that is the point: the hours the old 7 AM – 10 PM window simply deleted are now one
          scroll away rather than absent. */}
      <div ref={body} className="relative flex min-h-0 flex-1 overflow-y-auto">
        <TimeGutter width={GUTTER} nowMinutes={isToday ? nowMinutes : null} />
        <GridColumn date={date} onPick={onCompose} onDropAt={onDropAt}>
          {blocks.map((block) => (
            <BlockChip
              key={block.id}
              block={block}
              size="day"
              onOpen={onOpenBlock}
              onDragBegin={onDragBegin}
              dragging={drag?.moved === true && drag.block.id === block.id}
              actions={block.state === 'suggested' ? suggestionActions(block) : undefined}
            />
          ))}
          {proposal && (
            <GhostSlot
              label={`Drop “${proposal.label}” here`}
              startMinutes={proposal.startMinutes}
              endMinutes={proposal.endMinutes}
              size="day"
            />
          )}
          {/* The moved or resized block's proposal, in the SAME dashed treatment a dragged task
              gets. One drag vocabulary: dashed outline, proposed time on the right, and the
              thing being moved dimmed underneath until the pointer is released. */}
          {drag?.moved && drag.date === date && (
            <GhostSlot
              label={drag.block.title}
              startMinutes={drag.startMinutes}
              endMinutes={drag.endMinutes}
              size="day"
            />
          )}
          {isToday && nowMinutes !== null && <NowLine minutes={nowMinutes} />}
        </GridColumn>
      </div>
    </div>
  )
}
